/**
 * The step panel's modals (StepsGraph.tsx): adding or editing a tool or a trigger, a step's
 * output fields, and a hand-off. Each edits a draft and hands it back only on its button, so
 * Cancel or Escape leaves the step as it was. The team itself is still saved with the page's Save.
 *
 * Adding starts from a searchable list, not a blank form: picking something with no settings
 * adds it at once; otherwise the same modal moves on to its settings.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Lock, Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelectCombobox } from '@/components/ui/multi-select-combobox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  CAPABILITIES,
  CONNECTOR_COLORS,
  EVENTS,
  GATE,
  PLIABLE,
  RUN_BY_HAND,
  connectorLabel,
  connectorOf,
  eventLabel,
  fromList,
  mcpTools,
  runOnSchedule,
  triggerSource,
  type Capability,
  type Connection,
  type Edge,
  type Member,
  type Trigger,
} from './catalog';
import { AGENTS_PATH } from './data';

/** A connector's square: its colour and initial, and how many of the step's tools are on it when more than one. */
export function Glyph({ connector, count, large }: { connector: string; count?: number; large?: boolean }) {
  const label = connectorLabel(connector);
  return (
    <span
      title={count ? `${label}: ${count} tool${count > 1 ? 's' : ''}` : label}
      className={cn(
        'inline-flex flex-none items-center justify-center font-semibold text-white',
        large ? 'h-8 w-8 rounded-lg text-sm' : 'h-5 min-w-5 rounded px-1 text-[11px]',
      )}
      style={{ background: CONNECTOR_COLORS[connector] ?? '#64748B' }}
    >
      {label.charAt(0).toUpperCase()}
      {count && count > 1 ? ` ${count}` : ''}
    </span>
  );
}

/** What a trigger comes from: a connector's square, or a person or the clock. */
export function TriggerGlyph({ connector, large }: { connector: string; large?: boolean }) {
  if (connector !== 'user' && connector !== 'schedule') return <Glyph connector={connector} large={large} />;
  const Icon = connector === 'user' ? Play : Clock;
  return (
    <span className={cn('inline-flex flex-none items-center justify-center bg-muted text-muted-foreground', large ? 'h-8 w-8 rounded-lg' : 'h-5 w-5 rounded')}>
      <Icon className={large ? 'h-4 w-4' : 'h-3 w-3'} aria-hidden />
    </span>
  );
}

export function Pick({ value, onChange, options, placeholder, className, label }: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  label?: string;
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className={className ?? 'w-full'}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A list typed as tags: Enter or a comma adds one, Backspace on an empty box takes the last off. */
export function TagInput({ value, onChange, placeholder, label }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; label: string }) {
  const [text, setText] = useState('');
  const add = () => {
    const more = text.split(',').map((s) => s.trim()).filter((s) => s && !value.includes(s));
    if (more.length) onChange([...value, ...more]);
    setText('');
  };
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-1.5 py-1 focus-within:ring-1 focus-within:ring-ring">
      {value.map((v) => (
        <span key={v} className="inline-flex items-center gap-0.5 rounded bg-muted py-0.5 pl-2 pr-0.5 text-xs">
          {v}
          <button type="button" aria-label={`Remove ${v}`} className="rounded p-0.5 text-muted-foreground hover:text-foreground" onClick={() => onChange(value.filter((x) => x !== v))}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        aria-label={label}
        value={text}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={add}
        className="min-w-[6rem] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : v === undefined || v === null || v === '' ? [] : [String(v)]);

/** A label over its control, with an optional line of help under it. */
function Field({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[13px]">{label}</Label>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

/** Every modal here: a header, its body, and a footer with Remove on the left and Cancel and the action on the right. */
function EditDialog({ onClose, icon, title, description, children, onRemove, removeLabel, onBack, submitLabel, onSubmit, canSubmit = true, hint, wide }: {
  onClose: () => void;
  icon?: React.ReactNode;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  onRemove?: () => void;
  removeLabel?: string;
  onBack?: () => void;
  submitLabel?: string;
  onSubmit?: () => void;
  canSubmit?: boolean;
  hint?: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn('gap-0 p-0', wide ? 'max-w-2xl' : 'max-w-xl')}>
        <DialogHeader className="flex-row items-center gap-3 space-y-0 p-5 pb-4 pr-12 text-left">
          {icon}
          <div className="min-w-0">
            <DialogTitle className="font-heading text-lg">{title}</DialogTitle>
            {description && <DialogDescription className="mt-1 text-[13px]">{description}</DialogDescription>}
          </div>
        </DialogHeader>
        <div className="px-5 pb-5">{children}</div>
        <footer className="mt-auto flex items-center gap-2 border-t bg-muted/40 px-5 py-3">
          {onRemove && (
            <Button variant="ghost" size="sm" className="px-1 text-destructive hover:text-destructive" onClick={onRemove}>
              {removeLabel}
            </Button>
          )}
          {onBack && (
            <Button variant="ghost" size="sm" className="px-1 text-muted-foreground" onClick={onBack}>
              Choose another
            </Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">{hint ?? (onSubmit ? 'Saved with the team' : null)}</span>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {onSubmit && (
            <Button disabled={!canSubmit} onClick={onSubmit}>
              {submitLabel}
            </Button>
          )}
        </footer>
      </DialogContent>
    </Dialog>
  );
}

/** A searchable list of choices, grouped. */
function Picker({ placeholder, groups }: {
  placeholder: string;
  groups: { key: string; heading: React.ReactNode; items: { key: string; glyph: React.ReactNode; label: string; help?: string; taken?: boolean; onPick: () => void }[] }[];
}) {
  return (
    // Plain substring matching: cmdk's fuzzy default matches "tick" in "prod-triage-slack".
    <Command className="rounded-lg border" filter={(value, search) => (value.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0)}>
      <CommandInput placeholder={placeholder} autoFocus />
      <CommandList className="max-h-[50vh]">
        <CommandEmpty>Nothing matches.</CommandEmpty>
        {groups.map((g) => (
          <CommandGroup key={g.key} heading={g.heading}>
            {g.items.map((i) => (
              <CommandItem key={i.key} value={`${g.key} ${i.label}`} disabled={i.taken} onSelect={i.onPick} className="gap-3 py-2">
                {i.glyph}
                <div className="min-w-0 flex-1">
                  <div className="text-sm">{i.label}</div>
                  {i.help && <div className="text-xs text-muted-foreground">{i.help}</div>}
                </div>
                {i.taken && <span className="text-xs text-muted-foreground">Already added</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}

const groupHeading = (name: string, connector: string) => (
  <span>
    <span className="font-semibold text-foreground">{name}</span> {connector !== PLIABLE && connectorLabel(connector)}
  </span>
);

/** Which of an MCP server's listed tools the step may call. A granted tool the server no longer lists stays visible, to untick. */
function McpToolPicker({ tools, value, onChange }: { tools: ReturnType<typeof mcpTools>; value: string[]; onChange: (v: string[]) => void }) {
  const gone = value.filter((name) => !tools.some((t) => t.name === name));
  const rows = [...tools, ...gone.map((name) => ({ name, description: 'No longer listed by the server', read_only: false }))];
  if (!rows.length) return <p className="text-sm text-muted-foreground">The server listed no tools. Use Refresh tools on the connection’s page.</p>;
  return (
    <div className="space-y-2">
      {rows.map((t) => (
        <label key={t.name} className="flex items-start gap-2.5 text-sm">
          <Checkbox className="mt-0.5" checked={value.includes(t.name)} onCheckedChange={(on) => onChange(on ? [...value, t.name] : value.filter((n) => n !== t.name))} />
          <span className="min-w-0">
            <code className="text-xs">{t.name}</code>
            {!t.read_only && <span className="ml-2 text-xs text-amber-600">may change things</span>}
            {t.description && <span className="block text-xs text-muted-foreground line-clamp-2">{t.description}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

/** Add a tool (initial null: pick one first) or edit one. */
export function ToolDialog({ initial, granted, connections, onSubmit, onRemove, onClose }: {
  initial: Capability | null;
  /** The step's tools, to mark what it already has. */
  granted: Capability[];
  connections: Connection[];
  onSubmit: (c: Capability) => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Capability | null>(initial);

  if (!draft) {
    const sources = [{ name: PLIABLE, connector: PLIABLE }, ...connections.filter((c) => CAPABILITIES[c.connector])];
    const pick = (source: { name: string; connector: string }, capability: string, hasParams: boolean) => {
      // An MCP server's tools start as the ones it says only read; the rest are ticked by hand.
      const conn = connections.find((c) => c.name === source.name);
      const params = source.connector === 'mcp' ? { tools: mcpTools(conn).filter((t) => t.read_only).map((t) => t.name) } : {};
      const c = { connection: source.name, capability, params };
      if (hasParams || source.connector === 'mcp') setDraft(c);
      else onSubmit(c);
    };
    return (
      <EditDialog onClose={onClose} title="Add a tool" description="What else this step may do. Tools that need settings ask for them next." hint={<Link to={`${AGENTS_PATH}/connections`} className="underline">Add a connection</Link>}>
        <Picker
          placeholder="Search tools"
          groups={sources.map((s) => ({
            key: s.name,
            heading: groupHeading(s.name, s.connector),
            items: (CAPABILITIES[s.connector] ?? []).map((spec) => ({
              key: spec.capability,
              glyph: <Glyph connector={s.connector} />,
              label: spec.label,
              taken: granted.some((g) => g.connection === s.name && g.capability === spec.capability),
              onPick: () => pick(s, spec.capability, spec.params.length > 0),
            })),
          }))}
        />
      </EditDialog>
    );
  }

  const connector = connectorOf(draft.connection, connections);
  const spec = CAPABILITIES[connector]?.find((s) => s.capability === draft.capability);
  const setParam = (key: string, value: string | string[]) => {
    const params = { ...draft.params };
    if (Array.isArray(value) ? value.length : value) params[key] = value;
    else delete params[key];
    setDraft({ ...draft, params });
  };
  const tools = asList(draft.params.tools);
  const ready = connector === 'mcp' ? tools.length > 0 : !(spec?.params ?? []).some((p) => p.required && !asList(draft.params[p.key]).length);
  return (
    <EditDialog
      onClose={onClose}
      icon={<Glyph connector={connector} large />}
      title={spec?.label ?? draft.capability}
      description={`On ${draft.connection === PLIABLE ? 'Pliable' : draft.connection}.`}
      onRemove={onRemove}
      removeLabel="Remove tool"
      onBack={initial ? undefined : () => setDraft(null)}
      submitLabel={initial ? 'Update tool' : 'Add tool'}
      canSubmit={ready}
      onSubmit={() => onSubmit(draft)}
    >
      {connector === 'mcp' ? (
        <Field label="May call">
          <McpToolPicker tools={mcpTools(connections.find((c) => c.name === draft.connection))} value={tools} onChange={(v) => setParam('tools', v)} />
        </Field>
      ) : spec?.params.length ? (
        <div className="space-y-4">
          {spec.params.map((p) => (
            <Field key={p.key} label={p.required ? p.label : `${p.label} (optional)`}>
              {p.options ? (
                <MultiSelectCombobox
                  options={[...new Set([...p.options, ...asList(draft.params[p.key])])].map((o) => ({ value: o, label: o }))}
                  value={asList(draft.params[p.key])}
                  placeholder={p.placeholder}
                  onValueChange={(v) => setParam(p.key, v)}
                />
              ) : p.kind === 'list' ? (
                <TagInput label={p.label} value={asList(draft.params[p.key])} placeholder={p.placeholder} onChange={(v) => setParam(p.key, v)} />
              ) : (
                <Input aria-label={p.label} value={String(draft.params[p.key] ?? '')} placeholder={p.placeholder} onChange={(e) => setParam(p.key, e.target.value.trim())} />
              )}
            </Field>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nothing to set for this tool.</p>
      )}
    </EditDialog>
  );
}

/** Add a trigger (initial null: pick one first) or edit one. */
export function TriggerDialog({ initial, step, existing, connections, onSubmit, onRemove, onClose }: {
  initial: Trigger | null;
  step: string;
  existing: Trigger[];
  connections: Connection[];
  onSubmit: (t: Trigger) => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Trigger | null>(initial);
  // Conditions as rows, so a half-typed one (no field yet) survives until it's finished or removed.
  const [rows, setRows] = useState<[string, string[]][]>(() => Object.entries(initial?.filter ?? {}).map(([k, v]) => [k, asList(v)]));

  if (!draft) {
    const sources = connections.filter((c) => EVENTS[c.connector]);
    return (
      <EditDialog onClose={onClose} title={`What starts ${step}?`} description="A step can have several triggers; any one of them starts a run. To start it after another step, use the + under that step.">
        <Picker
          placeholder="Search triggers"
          groups={[
            {
              key: 'pliable',
              heading: <span className="font-semibold text-foreground">From Pliable</span>,
              items: [
                {
                  key: 'user',
                  glyph: <TriggerGlyph connector="user" />,
                  label: 'Run by hand',
                  help: 'An Admin starts it from Pliable',
                  taken: existing.some((t) => t.source === 'user'),
                  onPick: () => onSubmit({ ...RUN_BY_HAND }),
                },
                { key: 'schedule', glyph: <TriggerGlyph connector="schedule" />, label: 'On a schedule', help: 'Asks when, next', onPick: () => setDraft(runOnSchedule()) },
              ],
            },
            ...sources.map((c) => ({
              key: c.name,
              heading: groupHeading(c.name, c.connector),
              items: EVENTS[c.connector]!.map((e) => ({
                key: e.event,
                glyph: <Glyph connector={c.connector} />,
                label: e.label,
                onPick: () => setDraft({ source: 'connection' as const, connection: c.name, event: e.event }),
              })),
            })),
          ]}
        />
      </EditDialog>
    );
  }

  const source = triggerSource(draft, connections);
  const common = {
    onClose,
    icon: <TriggerGlyph connector={source} large />,
    onRemove,
    removeLabel: 'Remove trigger',
    onBack: initial ? undefined : () => setDraft(null),
    submitLabel: initial ? 'Update trigger' : 'Add trigger',
  };
  if (draft.source === 'user')
    return (
      <EditDialog {...common} title="Run by hand" description="An Admin starts it from Pliable.">
        <p className="text-sm text-muted-foreground">Nothing to set for this trigger.</p>
      </EditDialog>
    );
  if (draft.source === 'schedule')
    return (
      <EditDialog {...common} title="On a schedule" canSubmit={!!draft.cron?.trim()} onSubmit={() => onSubmit({ ...draft, cron: draft.cron!.trim() })}>
        <div className="space-y-4">
          <Field label="When (cron)" help="Minute, hour, day of month, month, weekday. 0 9 * * 1 is Mondays at 9:00.">
            <Input aria-label="Cron" className="font-mono" value={draft.cron ?? ''} placeholder="0 9 * * 1" onChange={(e) => setDraft({ ...draft, cron: e.target.value })} />
          </Field>
          <Field label="Timezone" help="Blank means UTC.">
            <Input aria-label="Timezone" value={draft.timezone ?? ''} placeholder="America/New_York" onChange={(e) => setDraft({ ...draft, timezone: e.target.value.trim() || null })} />
          </Field>
        </div>
      </EditDialog>
    );

  const done = () => {
    const filter = Object.fromEntries(rows.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), v.length > 1 ? v : (v[0] ?? '')]));
    onSubmit({ ...draft, filter, work_item_key: draft.work_item_key?.trim() || null });
  };
  return (
    <EditDialog {...common} title={eventLabel(source, draft.event)} description={`On ${draft.connection}.`} onSubmit={done} wide>
      <div className="space-y-5">
        <Field label="Only if" help="With no conditions, every one of these events starts it.">
          <div className="space-y-2">
            {rows.map(([k, v], i) => (
              <div key={i} className="flex items-start gap-2">
                <Input
                  aria-label="Event field"
                  className="w-36"
                  value={k}
                  placeholder={source === 'slack' ? 'channel' : 'priority'}
                  onChange={(e) => setRows(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))}
                />
                <span className="pt-2 text-sm text-muted-foreground">is</span>
                <div className="min-w-0 flex-1">
                  <TagInput label={`Values for ${k || 'this field'}`} value={v} placeholder="any of these" onChange={(nv) => setRows(rows.map((r, j) => (j === i ? [r[0], nv] : r)))} />
                </div>
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Remove this condition" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button variant="ghost" size="sm" className="-ml-2 h-7 px-2 text-xs text-muted-foreground" onClick={() => setRows([...rows, ['', []]])}>
              + Add a condition
            </Button>
          </div>
        </Field>
        <Field label="Same work when (optional)" help="An event that comes again joins the open work it started, instead of starting new work.">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>the event’s</span>
            <Input
              aria-label="Event field that groups repeats"
              className="w-40 text-foreground"
              value={draft.work_item_key ?? ''}
              placeholder={source === 'datadog' ? 'alert_id' : ''}
              onChange={(e) => setDraft({ ...draft, work_item_key: e.target.value })}
            />
            <span>matches</span>
          </div>
        </Field>
      </div>
    </EditDialog>
  );
}

/** A step's output fields, all at once. */
export function OutputDialog({ step, edges, onSubmit, onClose }: {
  step: Member;
  edges: Edge[];
  onSubmit: (fields: Record<string, string[]>) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<[string, string[]][]>(() => Object.entries(step.output_fields));
  const uses = (field: string) => edges.filter((e) => e.from === step.name && field && field in e.when);
  return (
    <EditDialog
      onClose={onClose}
      title={`What ${step.name} fills in`}
      description="Besides its summary. Hand-offs choose the next step on these. A field with no values listed takes any text."
      submitLabel="Update output"
      onSubmit={() => onSubmit(Object.fromEntries(rows.filter(([k]) => k.trim()).map(([k, v]) => [k.trim(), v])))}
      wide
    >
      <div className="grid grid-cols-[10rem_minmax(0,1fr)_auto] items-start gap-x-2 gap-y-3">
        <span className="text-[13px] font-medium text-muted-foreground">Field</span>
        <span className="text-[13px] font-medium text-muted-foreground">Allowed values</span>
        <span />
        {rows.map(([k, v], i) => (
          <div key={i} className="contents">
            <Input aria-label="Field name" value={k} placeholder="action" onChange={(e) => setRows(rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
            <div className="space-y-1">
              <TagInput label={`Allowed values for ${k || 'this field'}`} value={v} placeholder="Any text" onChange={(nv) => setRows(rows.map((r, j) => (j === i ? [r[0], nv] : r)))} />
              {uses(k).map((e, n) => (
                <p key={n} className="text-xs" style={{ color: GATE }}>
                  The hand-off to {e.to} runs when this is “{fromList(e.when[k])}”.
                </p>
              ))}
            </div>
            <Button variant="ghost" size="icon" className="h-9 w-9" aria-label={`Remove ${k || 'this field'}`} onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button variant="ghost" size="sm" className="-ml-2 mt-2 h-7 px-2 text-xs text-muted-foreground" onClick={() => setRows([...rows, ['', []]])}>
        + Add a field
      </Button>
    </EditDialog>
  );
}

const NONE = '__none__';
type GateChoice = 'none' | 'app' | 'slack';

/** A hand-off, read as a sentence with its blanks to fill in. */
export function HandoffDialog({ initial, steps, slack, onSubmit, onRemove, onClose }: {
  initial: Edge;
  steps: Member[];
  slack: Connection[];
  onSubmit: (e: Edge) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [e, setE] = useState<Edge>(initial);
  const from = steps.find((s) => s.name === e.from);
  const [field, value] = Object.entries(e.when)[0] ?? ['', ''];
  const allowed = (from && field && from.output_fields[field]) || [];
  const stepOptions = steps.map((s) => ({ value: s.name, label: s.name }));
  const gate: GateChoice = !e.gate ? 'none' : e.gate.connection != null ? 'slack' : 'app';
  const setGate = (g: GateChoice) => setE({ ...e, gate: g === 'none' ? null : g === 'app' ? {} : { connection: e.gate?.connection ?? slack[0]?.name ?? '', channel: e.gate?.channel ?? '' } });
  const ready = !!e.from && !!e.to && (gate !== 'slack' || (!!e.gate?.connection && !!e.gate?.channel));
  const sentence = 'flex flex-wrap items-center gap-2 text-sm';
  return (
    <EditDialog
      onClose={onClose}
      title="Hand-off"
      description="Which step runs next, and whether a person approves it first."
      onRemove={onRemove}
      removeLabel="Remove hand-off"
      submitLabel="Update hand-off"
      canSubmit={ready}
      onSubmit={() => onSubmit(e)}
      wide
    >
      <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-x-4 gap-y-5">
        <span className="pt-2 text-[13px] font-medium">When</span>
        <div className={sentence}>
          <Pick label="Step" className="w-auto min-w-[9rem]" value={e.from} options={stepOptions} placeholder="a step" onChange={(v) => setE({ ...e, from: v, when: {} })} />
          <span className="text-muted-foreground">finishes with</span>
          <Pick
            label="Output field"
            className="w-auto min-w-[8rem]"
            value={field || NONE}
            options={[{ value: NONE, label: 'any output' }, ...Object.keys(from?.output_fields ?? {}).filter(Boolean).map((k) => ({ value: k, label: k }))]}
            onChange={(k) => setE({ ...e, when: k === NONE ? {} : { [k]: from?.output_fields[k]?.[0] ?? '' } })}
          />
          {field && (
            <>
              <span className="text-muted-foreground">set to</span>
              {allowed.length ? (
                <Pick label="Value" className="w-auto min-w-[8rem]" value={String(value)} options={allowed.map((a) => ({ value: a, label: a }))} onChange={(v) => setE({ ...e, when: { [field]: v } })} />
              ) : (
                <Input aria-label="Value" className="w-40" value={String(value ?? '')} onChange={(ev) => setE({ ...e, when: { [field]: ev.target.value } })} />
              )}
            </>
          )}
        </div>

        <span className="pt-2 text-[13px] font-medium">First</span>
        <RadioGroup value={gate} onValueChange={(g) => setGate(g as GateChoice)} className="gap-2">
          {([
            ['none', 'No approval'],
            ['app', 'An Admin approves in Pliable'],
            ['slack', 'An Admin approves in Pliable or Slack'],
          ] as const).map(([g, label]) => (
            <div key={g} className={cn('rounded-lg border px-3 py-2.5', gate === g && g !== 'none' && 'border-[#C2410C]/60 bg-[#C2410C]/5')}>
              <label className="flex items-center gap-2.5 text-sm">
                <RadioGroupItem value={g} />
                {g !== 'none' && <Lock className="h-3.5 w-3.5" style={{ color: GATE }} aria-hidden />}
                {label}
              </label>
              {g === 'slack' && gate === 'slack' && (
                <div className="mt-3 grid grid-cols-2 gap-3 pl-6">
                  <Field label="Slack bot">
                    <Pick
                      label="Slack bot"
                      value={e.gate?.connection ?? ''}
                      placeholder={slack.length ? 'Choose a bot' : 'Add a Slack connection first'}
                      options={slack.map((c) => ({ value: c.name, label: c.name }))}
                      onChange={(connection) => setE({ ...e, gate: { ...e.gate!, connection } })}
                    />
                  </Field>
                  <Field label="Channel ID">
                    <Input aria-label="Channel ID" value={e.gate?.channel ?? ''} placeholder="C0123456789" onChange={(ev) => setE({ ...e, gate: { ...e.gate!, channel: ev.target.value.trim() } })} />
                  </Field>
                </div>
              )}
            </div>
          ))}
        </RadioGroup>

        <span className="pt-2 text-[13px] font-medium">Then</span>
        <div className={sentence}>
          <Pick label="Next step" className="w-auto min-w-[9rem]" value={e.to} options={stepOptions} placeholder="a step" onChange={(to) => setE({ ...e, to })} />
          <span className="text-muted-foreground">runs</span>
        </div>
      </div>
    </EditDialog>
  );
}
